/**
 * Data Capture Mutations
 *
 * Three mutations for the Data Capture tab:
 *   - useAddDataCaptureNote   — insert a quality-check note
 *   - useUploadDataCaptureImage — presigned S3 upload + metadata write
 *   - useDeleteDataCaptureImage — remove an uploaded image
 *
 * All three invalidate the data-capture query on success.
 */

import {
  addDataCaptureNote,
  completeDataCaptureImageUpload,
  deleteDataCaptureImage,
  getDataCaptureImageUploadUrl,
} from '@/domains/jobs/api/dataCaptureApi';
import { queryKeys } from '@/lib/query-client';
import { uploadToS3Presigned } from '@/shared/lib/presignedUpload';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export function useAddDataCaptureNote(jobId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (note: string) => addDataCaptureNote(jobId, { note }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.dataCapture(String(jobId)) });
    },
    meta: {
      successTitle: 'Note Added',
      successMessage: 'Data capture note added successfully',
    },
  });
}

export function useUploadDataCaptureImage(jobId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const presigned = await getDataCaptureImageUploadUrl(jobId, {
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
      });
      await uploadToS3Presigned(presigned, file);
      return completeDataCaptureImageUpload(jobId, {
        object_key: presigned.object_key,
        content_type: file.type || 'application/octet-stream',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.dataCapture(String(jobId)) });
    },
    meta: {
      successTitle: 'Image Uploaded',
      successMessage: 'Data capture image uploaded successfully',
    },
  });
}

export function useDeleteDataCaptureImage(jobId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (imageId: number) => deleteDataCaptureImage(jobId, imageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs.dataCapture(String(jobId)) });
    },
    meta: {
      successTitle: 'Image Removed',
      successMessage: 'Data capture image removed successfully',
    },
  });
}
